import { useQuery } from "@tanstack/react-query";
import { useGetIdentity, useGetList, type Identifier } from "ra-core";
import { useCallback, useEffect, useId } from "react";

import { getSupabaseClient } from "../../providers/supabase/supabase";
import { monthStart, type CallMission, type RankingRow } from "./callPlan";

const LIVE_TABLES = ["gula_call_missions", "gula_call_log"];

const RANKING_NUMBERS = [
  "goal",
  "pending",
  "today_calls",
  "today_contacts",
  "today_points",
  "calls",
  "contacts",
  "sales",
  "points",
] as const;

/** Ranking of a month (YYYY-MM-01): points add up all month, nothing is reset. */
export const useRanking = (month: string, enabled = true) =>
  useQuery({
    queryKey: ["gula_call_ranking_month", month],
    enabled,
    refetchInterval: 60_000,
    queryFn: async (): Promise<RankingRow[]> => {
      const { data, error } = await getSupabaseClient().rpc(
        "gula_call_ranking_month",
        { p_month: month },
      );
      if (error) throw new Error(error.message);
      // bigint columns can come back as strings
      return ((data ?? []) as Record<string, unknown>[]).map((row) => {
        const numbers = Object.fromEntries(
          RANKING_NUMBERS.map((key) => [key, Number(row[key] ?? 0)]),
        );
        return { ...row, ...numbers } as unknown as RankingRow;
      });
    },
  });

/** Today's lists and this month's ranking for everyone, kept live. */
export const useCallPlan = () => {
  const missionsQuery = useGetList<CallMission>(
    "gula_call_missions_today",
    {
      pagination: { page: 1, perPage: 500 },
      sort: { field: "position", order: "ASC" },
    },
    { refetchInterval: 60_000 },
  );
  const rankingQuery = useRanking(monthStart());
  const { refetch: refetchMissions } = missionsQuery;
  const { refetch: refetchRanking } = rankingQuery;
  const reload = useCallback(() => {
    refetchMissions();
    refetchRanking();
  }, [refetchMissions, refetchRanking]);

  const channelName = `call-plan-${useId()}`;
  useEffect(() => {
    const supabase = getSupabaseClient();
    const channel = supabase.channel(channelName);
    for (const table of LIVE_TABLES) {
      channel.on(
        "postgres_changes",
        { event: "*", schema: "public", table },
        () => reload(),
      );
    }
    channel.subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [channelName, reload]);

  return {
    missions: missionsQuery.data ?? [],
    ranking: rankingQuery.data ?? [],
    isPending: missionsQuery.isPending,
    reload,
  };
};

const rpc = async <T>(name: string, args: Record<string, unknown>) => {
  const { data, error } = await getSupabaseClient().rpc(name, args);
  if (error) throw new Error(error.message);
  return data as T;
};

/** Puts leads in someone's list for today; returns how many were free. */
export const takeCalls = (
  clientIds: Identifier[],
  salesId: Identifier | null | undefined,
  bucket: string,
) =>
  rpc<number>("gula_take_calls", {
    p_client_ids: clientIds.map(Number),
    p_sales_id: salesId == null ? null : Number(salesId),
    p_bucket: bucket,
  });

export type QuickResult = "no_answer" | "contacted" | "skip";

export const recordResult = (clientId: Identifier, result: QuickResult) =>
  rpc<void>("gula_call_result", {
    p_client_id: Number(clientId),
    p_result: result,
  });

export const releaseCall = (missionId: Identifier) =>
  rpc<void>("gula_release_call", { p_mission_id: Number(missionId) });

export const setCallGoal = (salesId: Identifier, goal: number) =>
  rpc<void>("gula_set_call_goal", {
    p_sales_id: Number(salesId),
    p_goal: goal,
  });

/** Open calls in the signed-in user's list today (header badge). */
export const useMyPendingCalls = () => {
  const { identity } = useGetIdentity();
  const { data = [] } = useGetList<CallMission>(
    "gula_call_missions_today",
    {
      filter: { sales_id: identity?.id, "done_at@is": null },
      pagination: { page: 1, perPage: 500 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: identity?.id != null, refetchInterval: 30_000 },
  );
  return data.length;
};
